import { createSelector } from 'reselect';

import {
  DataModelTransition,
  DataModelTransitionParams,
} from '../../domain/dataModelTransition';
import {
  extractSisTypeCodesFromModelTransitionRows,
  filterModelTransitionRowsByIds,
} from '../../domain/modelTransition/filterRowsByIds';
import {
  getModelTransitionRowId,
  resolveModelTransitionMainField,
} from '../../domain/modelTransition/mainField';
import {
  Column,
  Item,
  LoadingState,
  MAIN_FIELD_TYPE,
} from '../../domain/schemas';
import { ShuOption } from '../../domain/shu';
import { filterSisTypeCodesByPresentCodes } from '../../domain/sis/sisTypeTableFilter';

import { filterModelName } from '../../utils/filterModelName';
import { filterTransitionDataByFields } from '../../utils/filterTransitionDataByFields';
import { parseNumber } from '../../utils/parseNumber';
import {
  filterRowsKeepingLeadingAverage,
  nonTransitionStringCellValue,
} from '../../utils/tableHeaderSubMenuMultiFilter';
import {
  modelTransitionCurrentFieldCodesSelector,
  modelTransitionReportsFilteredKiTagIdsSelector,
  modelTransitionReportsFilteredSisTypeCodesSelector,
  modelTransitionReportsModelNameFilterSelector,
  modelTransitionReportsSelectedKiSelector,
  modelTransitionReportsSettingIsHeatmapEnabledSelector,
  modelTransitionTableHeaderSubMenuMultiFilterSelector,
} from '../ui/modelTransitionReportsSetting';

// Action Types

const FETCH_DATA_MODEL_TRANSITION = 'FETCH_DATA_MODEL_TRANSITION' as const;
const FETCH_DATA_MODEL_TRANSITION_REQUEST =
  'FETCH_DATA_MODEL_TRANSITION_REQUEST' as const;
const FETCH_DATA_MODEL_TRANSITION_SUCCESS =
  'FETCH_DATA_MODEL_TRANSITION_SUCCESS' as const;
const RENEW_DATA_MODEL_TRANSITION = 'RENEW_DATA_MODEL_TRANSITION' as const;

export const DataModelTransitionActionTypes = {
  FETCH_DATA_MODEL_TRANSITION,
  FETCH_DATA_MODEL_TRANSITION_REQUEST,
  FETCH_DATA_MODEL_TRANSITION_SUCCESS,
  RENEW_DATA_MODEL_TRANSITION,
};

// Action Creators

function fetchDataModelTransitionAction(
  shuOption: ShuOption,
  params: DataModelTransitionParams
) {
  return {
    type: FETCH_DATA_MODEL_TRANSITION,
    payload: { shuOption, params },
  };
}

function fetchDataModelTransitionRequestAction() {
  return {
    type: FETCH_DATA_MODEL_TRANSITION_REQUEST,
  };
}

function fetchDataModelTransitionSuccessAction(
  dataModelTransition: DataModelTransition
) {
  return {
    type: FETCH_DATA_MODEL_TRANSITION_SUCCESS,
    payload: { dataModelTransition },
  };
}

function renewDataModelTransitionAction() {
  return {
    type: RENEW_DATA_MODEL_TRANSITION,
  };
}

export const DataModelTransitionActionCreators = {
  fetchDataModelTransitionAction,
  fetchDataModelTransitionRequestAction,
  fetchDataModelTransitionSuccessAction,
  renewDataModelTransitionAction,
};

// Actions

export type FetchDataModelTransitionAction = ReturnType<
  typeof fetchDataModelTransitionAction
>;

type DataModelTransitionAction =
  | FetchDataModelTransitionAction
  | ReturnType<typeof fetchDataModelTransitionRequestAction>
  | ReturnType<typeof fetchDataModelTransitionSuccessAction>
  | ReturnType<typeof renewDataModelTransitionAction>;

// State

type DataModelTransitionState = {
  loadingState: LoadingState;
  dataModelTransition?: DataModelTransition;
  isInitialLoad: boolean;
};

const initialState: DataModelTransitionState = {
  loadingState: 'prepare',
  dataModelTransition: undefined,
  isInitialLoad: true,
};

// Selector

export function dataModelTransitionAllSelector(rootState: {
  dataModelTransition: DataModelTransitionState;
}) {
  return rootState.dataModelTransition;
}

export const dataModelTransitionSelector = createSelector(
  [
    dataModelTransitionAllSelector,
    modelTransitionReportsModelNameFilterSelector,
    modelTransitionReportsFilteredKiTagIdsSelector,
    modelTransitionReportsFilteredSisTypeCodesSelector,
    modelTransitionCurrentFieldCodesSelector,
  ],
  (
    dataModelTransition,
    modelNameFilter,
    filteredKiTagIds,
    filteredSisTypeCodes,
    currentFieldCodes
  ) => {
    const modelTransition = dataModelTransition.dataModelTransition;
    if (!modelTransition) {
      return;
    }

    const data = modelTransition.data;
    const rows = data.rows;
    const mainField = resolveModelTransitionMainField(
      modelTransition.setting.mainField
    );
    const modelNamePredicate = filterModelName(modelNameFilter);
    const filteredRowsByMainField =
      mainField === MAIN_FIELD_TYPE.KITAG
        ? filterModelTransitionRowsByIds(
            rows,
            filteredKiTagIds.map(String),
            MAIN_FIELD_TYPE.KITAG
          )
        : mainField === MAIN_FIELD_TYPE.TYPE
        ? filterModelTransitionRowsByIds(
            rows,
            filterSisTypeCodesByPresentCodes(
              filteredSisTypeCodes,
              extractSisTypeCodesFromModelTransitionRows(rows)
            ),
            MAIN_FIELD_TYPE.TYPE
          )
        : modelNameFilter
        ? rows.filter((row) =>
            modelNamePredicate(row.dataForNonTransition.at(0)?.value ?? '')
          )
        : rows;

    const result: DataModelTransition = {
      ...modelTransition,
      data: {
        ...data,
        rows: filteredRowsByMainField,
      },
    };

    // 項目絞込の処理（導入後推移と同じ filterTransitionDataByFields）
    // 空配列はメニュー操作中の一時状態のため、絞り込みしない（閉じたとき onClose で ['all'] に戻る）
    if (currentFieldCodes.length === 0 || currentFieldCodes.includes('all')) {
      return result;
    }
    const filteredData = filterTransitionDataByFields(
      result,
      currentFieldCodes
    );
    // 項目絞込が適用できない場合でも、機種名/タグの行フィルタは維持する
    return filteredData ?? result;
  }
);

export function dataModelTransitionColumnsForTransitionSelector(rootState: {
  dataModelTransition: DataModelTransitionState;
}) {
  return (
    rootState.dataModelTransition.dataModelTransition?.data
      .columnsForTransition ?? []
  );
}

export function dataModelTransitionColumnsForNonTransitionSelector(rootState: {
  dataModelTransition: DataModelTransitionState;
}) {
  return (
    rootState.dataModelTransition.dataModelTransition?.data
      .columnsForNonTransition ?? []
  );
}

/**
 * 機種別推移の現在表示中のデータの検索条件を取得する
 * @returns 現在の検索条件
 */
export const dataModelTransitionSettingsSelector = createSelector(
  dataModelTransitionSelector,
  (data) => {
    return data?.setting;
  }
);

/**
 * テーブルの行データを取得する
 * @returns テーブルの行データ
 */
const dataModelTransitionRowsSelector = createSelector(
  dataModelTransitionSelector,
  (data) => data?.data.rows
);

/**
 * 機種コードと機種名を機種コードをkeyとしたで取得する
 * @returns KiOptions
 */
export const dataModelTransitionKiOptionsSelector = createSelector(
  dataModelTransitionRowsSelector,
  dataModelTransitionSettingsSelector,
  (rows, setting) => {
    const mainField = resolveModelTransitionMainField(setting?.mainField);
    const rowOptions =
      rows?.flatMap((row) => {
        const rowId = getModelTransitionRowId(row.queryParameter, mainField);
        const rowName = row.dataForNonTransition.at(0)?.value;
        if (rowId === undefined || rowName === undefined) {
          return [];
        }
        return [[rowId, rowName] as const];
      }) ?? [];

    return Object.fromEntries(rowOptions);
  }
);

export const dataModelTransitionDataSearchConditionSelector = createSelector(
  dataModelTransitionAllSelector,
  (dataModelTransition) => dataModelTransition.dataModelTransition?.setting
);

export const dataModelTransitionLoadingStateSelector = createSelector(
  dataModelTransitionAllSelector,
  (dataModelTransition) => dataModelTransition.loadingState
);

/**
 * [機種別推移] 初回ロード状態を取得する
 */
export const dataModelTransitionIsInitialLoadSelector = createSelector(
  dataModelTransitionAllSelector,
  (dataModelTransition) => dataModelTransition.isInitialLoad
);

export const addHeatmapBackgroundColorToOutField = (
  columnsForTransition: Column[],
  rows: DataModelTransition['data']['rows']
) => {
  if (rows.length < 40) {
    return rows;
  }
  const outFieldIndex = columnsForTransition.findIndex((x) => x.code === 'out');
  if (outFieldIndex === -1) {
    return rows;
  }

  const rowsWithOnlyOutValue = rows.map((x) =>
    (x.dataForTransition.at(outFieldIndex) ?? []).map((x) => x.value)
  );
  if (rowsWithOnlyOutValue.length === 0) {
    return rows;
  }

  const borderNumbersPerDate = rowsWithOnlyOutValue
    // transpose
    .reduce(
      (accum: string[][], curr: string[]) =>
        accum.map((xs, i) => [...xs, curr[i]]),
      [...Array(rowsWithOnlyOutValue.at(0)?.length ?? 0)].map((_) => [])
    )
    // filter hypen out and convert string to number
    .map((xs: string[]) =>
      xs.filter((x: string) => x !== '-').map((x: string) => parseNumber(x))
    )
    // top10, top20, bottom20, bottom10
    .map((xs: number[]) => {
      xs.sort((a, b) => a - b);
      const top10 = xs[xs.length - 10];
      const top20 = xs[xs.length - 20];
      const bottom10 = xs[9];
      const bottom20 = xs[19];

      return [top10, top20, bottom20, bottom10];
    });

  const addBackgroundColor =
    (borderNumbersPerDate: number[][]) =>
    (dataForTransition: Item[], dataTransitionFieldIndex: number) => {
      if (dataTransitionFieldIndex !== outFieldIndex) {
        return dataForTransition;
      }
      return dataForTransition.map((x: Item, i: number) => {
        const [top10, top20, bottom20, bottom10] = borderNumbersPerDate[i];
        const val = parseNumber(x.value);
        if (Number.isNaN(val)) {
          return x;
        }

        const backgroundColor =
          val >= top10
            ? '#a8d3ff'
            : val >= top20
            ? '#d7eefd'
            : val <= bottom10
            ? '#ffb3b3'
            : val <= bottom20
            ? '#fddddb'
            : 'inherit';

        return {
          ...x,
          backgroundColor,
        };
      });
    };

  return rows.map((x) => ({
    ...x,
    dataForTransition: x.dataForTransition.map(
      addBackgroundColor(borderNumbersPerDate)
    ),
  }));
};

const dataModelTransitionHeatmappedSelector = createSelector(
  [
    dataModelTransitionSelector,
    modelTransitionReportsSettingIsHeatmapEnabledSelector,
    dataModelTransitionSettingsSelector,
  ],
  (dataModelTransition, isHeatmapEnabled, setting) => {
    const tableData = dataModelTransition?.data;

    if (tableData === undefined) {
      return {
        columnsForNonTransition: [],
        columnsForTransition: [],
        dates: [],
        rows: [],
      };
    }

    if (tableData.rows.length === 0) {
      return tableData;
    }

    const isKiOutput =
      resolveModelTransitionMainField(setting?.mainField) ===
      MAIN_FIELD_TYPE.KI_TUSHO_MEI;
    const heatmappedRows =
      isHeatmapEnabled && isKiOutput
        ? addHeatmapBackgroundColorToOutField(
            tableData.columnsForTransition,
            tableData.rows
          )
        : tableData.rows;

    return {
      ...tableData,
      rows: heatmappedRows,
    };
  }
);

export const dataModelTransitionCheckedFilteredDataSelector = createSelector(
  [
    dataModelTransitionHeatmappedSelector,
    modelTransitionReportsSelectedKiSelector,
    dataModelTransitionSettingsSelector,
  ],
  (tableData, checkedRowIds, setting) => {
    if (!checkedRowIds || checkedRowIds.length === 0) {
      return tableData;
    }

    const mainField = resolveModelTransitionMainField(setting?.mainField);
    return {
      ...tableData,
      rows: filterModelTransitionRowsByIds(
        tableData.rows,
        checkedRowIds,
        mainField
      ),
    };
  }
);

export const dataModelTransitionFilteredDataSelector = createSelector(
  [
    dataModelTransitionCheckedFilteredDataSelector,
    modelTransitionTableHeaderSubMenuMultiFilterSelector,
  ],
  (tableData, multiFilter) => {
    const rowsAfterChecked = tableData.rows;

    const rows = filterRowsKeepingLeadingAverage(
      rowsAfterChecked,
      false,
      multiFilter,
      (row, columnCode) =>
        nonTransitionStringCellValue(
          tableData.columnsForNonTransition,
          row,
          columnCode
        )
    );

    return {
      ...tableData,
      rows,
    };
  }
);

/**
 * レスポンスの検索条件からエリア一覧を取得する
 */
export const dataModelTransitionSettingAreasSelector = createSelector(
  dataModelTransitionSettingsSelector,
  (setting) => setting?.areas
);

// Reducer

export function dataModelTransitionReducer(
  state = initialState,
  action: DataModelTransitionAction
): DataModelTransitionState {
  switch (action.type) {
    case FETCH_DATA_MODEL_TRANSITION_REQUEST:
      return {
        ...state,
        loadingState: 'loading',
      };
    case FETCH_DATA_MODEL_TRANSITION_SUCCESS:
      return {
        ...state,
        loadingState: 'loaded',
        dataModelTransition: action.payload.dataModelTransition,
        isInitialLoad: false,
      };
    case RENEW_DATA_MODEL_TRANSITION:
      return initialState;
    default:
      return state;
  }
}
